import {
  BotIcon,
  CheckCircle2Icon,
  FlagIcon,
  MessageSquareIcon,
  WrenchIcon,
  XCircleIcon,
} from 'lucide-react';
import type { JSX } from 'react';

import type { ConversationEvent } from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';

/** Shorten an MCP tool name (mcp__server__tool → tool) for display. */
function shortToolName(name: string): string {
  return name.includes('__') ? name.split('__').pop()! : name;
}

function EventRow({ event }: { event: ConversationEvent }): JSX.Element | null {
  switch (event.kind) {
    case 'tool_call': {
      const isAgent = event.name === 'Agent';
      const Icon = isAgent ? BotIcon : WrenchIcon;
      return (
        <div className="flex items-start gap-2 py-1 text-xs">
          <Icon
            className={cn(
              'mt-0.5 size-3.5 shrink-0',
              isAgent ? 'text-violet-500' : 'text-info-500',
            )}
            aria-hidden
          />
          <span className="font-medium text-foreground">
            {shortToolName(event.name ?? '?')}
          </span>
          {event.input ? (
            <code className="min-w-0 truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[0.7rem] text-muted-foreground">
              {event.input}
            </code>
          ) : null}
        </div>
      );
    }
    case 'result':
      return (
        <div className="flex items-start gap-2 py-1 text-xs text-muted-foreground">
          <CheckCircle2Icon
            className="mt-0.5 size-3.5 shrink-0 text-success-500"
            aria-hidden
          />
          <span className="min-w-0 break-words">{event.content}</span>
        </div>
      );
    case 'error':
      return (
        <div className="flex items-start gap-2 rounded-md border-l-2 border-destructive bg-destructive/5 py-1 pl-2 text-xs text-destructive">
          <XCircleIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">{event.content}</span>
        </div>
      );
    case 'text':
      return (
        <div className="flex items-start gap-2 py-1 text-xs italic text-muted-foreground">
          <MessageSquareIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">{event.content}</span>
        </div>
      );
    case 'final':
      return (
        <div className="mt-1 flex items-start gap-2 rounded-md bg-muted/50 px-2 py-1.5 text-xs">
          <FlagIcon
            className="mt-0.5 size-3.5 shrink-0 text-foreground"
            aria-hidden
          />
          <span className="min-w-0 break-words font-medium text-foreground">
            {event.content}
          </span>
        </div>
      );
    default:
      return null;
  }
}

export function EventList({
  events,
}: {
  events: ConversationEvent[];
}): JSX.Element {
  if (events.length === 0) {
    return (
      <p className="py-2 text-xs italic text-muted-foreground">No events.</p>
    );
  }
  return (
    <div className="space-y-0.5">
      {events.map((e, i) => (
        <EventRow key={i} event={e} />
      ))}
    </div>
  );
}
